"use client";
import { useState, useRef, useEffect } from "react";
import { CheckCircle } from "lucide-react";
import {Metadata} from "next"; 
import Image from "next/image";

export const metadata: Metadata = {
    title: "Installation Guide ",
}

const installationData = {
  mobileStarter: {
    name: "Kisan Mithraa Mobile Starter",
    steps: [
      {
        id: 1,
        step: "Step 1",
        title: "Site Preparation",
        description: "Choose a dry, accessible location on the starter box. Ensure the area is protected from direct water exposure and has proper ventilation.",
        image: "/site.png"
      },
      {
        id: 2,
        step: "Step 2",
        title: "Mount the Control Box",
        description: "Securely mount the Kisan Mithraa control box on a main starter box at least 1-2 feet. Use the provided mounting brackets and screws.",
        image: "/mounting.png"
      },
      {
        id: 3,
        step: "Step 3",
        title: "Connect Power Supply",
        description: "Connect the main power supply to the Connect wires (with our video call support): R, Y, B → Phase wires; Two black wires → Terminals 93 & 94. Ensure proper earthing and use a circuit breaker for safety. Follow the color-coded wiring diagram.",
        image: "/power.jpg"
      },
      {
        id: 4,
        step: "Step 4",
        title: "Motor Connection",
        description: "Connect your motor's wires to the output terminals by inserting the three coils given by us. Tighten all connections properly to prevent loose contacts.",
        image: "/connection.jpg"
      },
      {
        id: 5,
        step: "Step 5",
        title: "Inserting Fuses",
        description: "Insert your fuses into the designated fuse holders for each phase (R, Y, B). Ensure they are securely in place to protect against electrical surges.",
        image: "/insertFuses.jpg"
      },
      {
        id: 6,
        step: "Step 6",
        title: "SIM Card & Mobile Setup",
        description: "Insert an active SIM card with balance into the device. Register your mobile number by calling the kisan mithraa technician. Test the connection with a test call.",
        image: "/sim_insert.png"
      },
      {
        id: 7,
        step: "Step 7",
        title: "Initial Testing",
        description: "Power on the system and test all functions. Check motor on/off control via mobile, verify sensor detection, and ensure all safety features are working properly.",
        image: "/testing.png"
      },
      {
        id: 8,
        step: "Step 8",
        title: "Final Verification",
        description: "Run a complete cycle test. Monitor the system for 24 hours to ensure stable operation. Save emergency contact numbers and keep the user manual accessible.",
        image: "/f_testing.png"
      }
    ]
  },
  remoteStarter: {
    name: "Kisan Mithraa Remote Starter",
    steps: [
      {
        id: 1,
        step: "Step 1",
        title: "Unbox and Inspect",
        description: "Carefully unbox the Remote Starter kit. Verify all components: receiver unit, remote control, mounting brackets, screws, and user manual are present.",
        image: "/remote_1.png"
      },
      {
        id: 2,
        step: "Step 2",
        title: "Install Receiver Unit",
        description: "Mount the receiver box near your starter panel in a dry location. Ensure the antenna has clear line of sight and is not blocked by metal objects for better signal reception.",
        image: "/r-mount.png"
      },
      {
        id: 3,
        step: "Step 3",
        title: "Power Connection",
        description: "Connect the power supply wires to the receiver unit. Follow the wiring diagram: Red wire to positive terminal, Black wire to negative/neutral. Ensure proper earthing connection.",
        image: "/r_remote.png"
      },
      {
        id: 4,
        step: "Step 4",
        title: "Motor Wiring",
        description: "Connect the receiver output terminals to your motor starter. Wire the relay contacts (NO, NC, COM) as per the circuit diagram. Double-check all connections before proceeding.",
        image: "/motor_remote.png"
      },
      {
        id: 5,
        step: "Step 5",
        title: "check connections",
        description: "Double-check all wiring connections for accuracy and tightness. Ensure there are no loose wires or exposed conductors that could cause shorts or malfunctions.",
        image: "/check.png"
      },
      {
        id: 6,
        step: "Step 6",
        title: "Remote Control Pairing",
        description: "Insert batteries into the remote control. Press and hold the pairing button on the receiver. Press the remote button to sync. LED indicator will confirm successful pairing.",
        image: "/pairing.png"
      },
      {
        id: 7,
        step: "Step 7",
        title: "Range Testing",
        description: "Test the remote control from various distances. Verify the maximum operating range matches specifications (typically 100-500 meters depending on model). Check for any signal interference.",
        image: "/range.png"
      },
      {
        id: 8,
        step: "Step 8",
        title: "Final System Check",
        description: "Perform complete on/off cycles from different locations. Verify motor responds correctly to remote commands. Test emergency stop function. Keep spare batteries accessible.",
        image: "/check.png"
      }
    ]
  }
};

export default function InstallationGuide() {
  type ProductKey = keyof typeof installationData;
  const [selectedProduct, setSelectedProduct] = useState<ProductKey>('mobileStarter');
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<Array<HTMLDivElement | null>>([]);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const currentProduct = installationData[selectedProduct];

  useEffect(() => {
    // Reset active step when switching products
    setActiveStep(0);
  }, [selectedProduct]);

  useEffect(() => {
    // Animate steps
    stepRefs.current.forEach((step, index) => {
      if (!step) return;
      
      if (index === activeStep) {
        step.style.transition = 'all 0.5s ease-out';
        step.style.transform = 'scale(1)';
        step.style.opacity = '1';
      } else {
        step.style.transform = 'scale(0.95)';
        step.style.opacity = '0.6';
      }
    });

    // Animate content on product switch
    if (contentRef.current) {
      contentRef.current.style.opacity = '0';
      contentRef.current.style.transform = 'translateY(20px)';
      
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.style.transition = 'all 0.5s ease-out';
          contentRef.current.style.opacity = '1';
          contentRef.current.style.transform = 'translateY(0)';
        }
      }, 100);
    }
  }, [activeStep, selectedProduct]);

  return (
    <section className="min-h-screen bg-white py-16 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-[#6BAF92] px-8 py-4 rounded-2xl mb-6">
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Easy Installation Guide
            </h1>
          </div>
          <p className="text-xl text-black max-w-2xl mx-auto">
            Follow these simple steps to install your Kisan Mithraa product in the field
          </p>
        </div>

        {/* Product Selector */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <button
            onClick={() => setSelectedProduct('mobileStarter')}
            className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 cursor-pointer ${
              selectedProduct === 'mobileStarter'
                ? 'bg-[#6BAF92] text-white shadow-lg scale-105'
                : 'bg-gray-200 text-black hover:bg-gray-300'
            }`}
          >
            Mobile Starter
          </button>
          <button
            onClick={() => setSelectedProduct('remoteStarter')}
            className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all duration-300 cursor-pointer ${
              selectedProduct === 'remoteStarter'
                ? 'bg-[#6BAF92] text-white shadow-lg scale-105'
                : 'bg-gray-200 text-black hover:bg-gray-300'
            }`}
          >
            Remote Starter
          </button>
        </div>

        {/* Product Name */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-black">{currentProduct.name}</h2>
        </div>

        <div ref={contentRef}>
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-black font-medium">Progress</span>
              <span className="text-sm text-black font-medium">{activeStep + 1} of {currentProduct.steps.length}</span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#6BAF92] to-[#7BA896] transition-all duration-500 ease-out"
                style={{ width: `${((activeStep + 1) / currentProduct.steps.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Installation Steps */}
          <div className="space-y-8 mb-12">
            {currentProduct.steps.map((step, index) => (
              <div
                key={step.id}
                ref={el => { stepRefs.current[index] = el; }}
                onClick={() => setActiveStep(index)}
                className={`cursor-pointer transition-all duration-300 ${
                  index === activeStep 
                    ? 'ring-4 ring-[#6BAF92] ring-opacity-50' 
                    : 'hover:shadow-lg'
                }`}
              >
                <div className="bg-white rounded-2xl shadow-md overflow-hidden border-2 border-gray-100">
                  <div className="grid md:grid-cols-2 gap-6">
                    
                    {/* Image */}
                    <div className="relative h-56 md:h-64 lg:h-100 overflow-hidden rounded-2xl">
                      {/* parent must be relative + have a valid height for Image fill to work */}
                      <Image
                        src={step.image}
                        alt={step.title}
                        fill
                        priority={index === activeStep}
                        className="object-cover object-center"
                      />
                       {index <= activeStep && (
                         <div className="absolute top-4 right-4">
                           <CheckCircle className="w-10 h-10 text-[#6BAF92] bg-white rounded-full" />
                         </div>
                       )}
                    </div>

                    {/* Content */}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="inline-block bg-[#6BAF92] px-4 py-2 rounded-lg mb-4 self-start">
                        <span className="text-white font-bold text-sm">{step.step}</span>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-black mb-4">
                        {step.title}
                      </h3>
                      
                      <p className="text-black leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center gap-4 mb-12">
            <button
              onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-black font-semibold rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous Step
            </button>
            
            <button
              onClick={() => setActiveStep(Math.min(currentProduct.steps.length - 1, activeStep + 1))}
              disabled={activeStep === currentProduct.steps.length - 1}
              className="px-6 py-3 bg-[#6BAF92] hover:bg-[#7BA896] text-white font-semibold rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next Step →
            </button>
          </div>
        </div>

        {/* Safety Tips */}
        <div className="bg-gradient-to-r from-[#6BAF92] to-[#7BA896] rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-white mb-6">Safety Tips</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <p className="text-white">Always turn off power before connecting wires</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <p className="text-white">Ensure proper earthing for electrical safety</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <p className="text-white">Keep the control box away from water exposure</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-white flex-shrink-0 mt-1" />
              <p className="text-white">Use recommended cable sizes for connections</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-white rounded-2xl p-8 shadow-md">
          <h3 className="text-3xl font-bold text-black mb-4">
            Need Installation Support?
          </h3>
          <p className="text-black mb-6 text-lg">
            Our technical team is ready to assist you with installation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/ContactUs"
              className="px-8 py-4 bg-[#6BAF92] hover:bg-[#7BA896] text-white text-lg font-semibold rounded-full shadow-lg transition-all duration-300 hover:scale-105"
            >
              Contact Support
            </a>
            <a 
              href="/Order"
              className="px-8 py-4 bg-white hover:bg-black text-black hover:text-white text-lg font-semibold rounded-full shadow-lg border-2 border-black transition-all duration-300 hover:scale-105"
            >
              Order Now
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}